import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { SubscriptionDataModel, SubscriptionModel } from "entities/shared/subscriptionModel";
import { useRequestCallbacks } from "shared/api/lib/api.hooks";
import { queryClient } from "shared/api/queryClient";
import { useAppDispatch } from "shared/lib/redux";

import { apiProfileAccessService, CheckApiProfileAccessResponseModel } from "../apiProfileAccess.api";

type UseSeaCheckApiProfileAccessQueryReturnType = {
  checkProfileSuccessResponse: SubscriptionDataModel | null | undefined;
  isCheckProfileAccessSuccess: boolean;
  isCheckProfileAccessError: boolean;
  isCheckProfileAccessLoading: boolean;
  resetSeaCheckApiProfileAccess: () => void;
};

type UseSeaCheckApiProfileAccessQueryParams = {
  requestOnMount?: boolean;
  setSubscription: (subscription: SubscriptionDataModel | null) => {
    type: string;
    payload: SubscriptionDataModel | null;
  };
};

const CHECK_API_PROFILE_ACCESS_QUERY_KEY = "check-api-profile-access-sea";

export const useSeaCheckApiProfileAccessQuery = ({
  requestOnMount = true,
  setSubscription,
}: UseSeaCheckApiProfileAccessQueryParams): UseSeaCheckApiProfileAccessQueryReturnType => {
  const dispatch = useAppDispatch();
  const [enabled, setEnabled] = useState(false);

  const meta = useQuery<CheckApiProfileAccessResponseModel, Error, SubscriptionDataModel | null>({
    queryFn: apiProfileAccessService.checkProfileAccess,
    queryKey: [CHECK_API_PROFILE_ACCESS_QUERY_KEY],
    select: (response) => new SubscriptionModel(response.data).data,
    enabled,
  });

  const { data, isSuccess, isError, isPending } = meta;

  const { setRequestCallbacks } = useRequestCallbacks<SubscriptionDataModel | null, Error>(meta);

  const resetQueries = () => {
    queryClient.resetQueries({ queryKey: [CHECK_API_PROFILE_ACCESS_QUERY_KEY] });
    dispatch(setSubscription(null));
  };

  useEffect(() => {
    if (!requestOnMount) return;
    setEnabled(true);

    setRequestCallbacks({
      onSuccess: (response) => dispatch(setSubscription(response)),
      onSettled: () => {
        setEnabled(false);
      },
    });
  }, []);

  return {
    checkProfileSuccessResponse: data,
    isCheckProfileAccessSuccess: isSuccess,
    isCheckProfileAccessError: isError,
    isCheckProfileAccessLoading: isPending,
    resetSeaCheckApiProfileAccess: resetQueries,
  };
};
